import { createContext, useCallback, useContext, useState } from 'react';

/**
 * 'UI Tips' preference (#1282).
 *
 * When ON (the default), the new-row entry fields show their hint cues: the
 * indigo required-empty outline (#1210/#1279) and the faded in/out placeholder
 * hue (#1281). When OFF, those cues go quiet on the new-row payee + amount
 * fields for a plain, calm form. The standard focus ring is never touched, so
 * keyboard focus stays visible either way.
 *
 * Mirrors the darkMode pattern in ThemeContext: a localStorage-backed boolean
 * with a context default so consumers render correctly even without a provider
 * (e.g. in unit tests), defaulting to tips-on.
 *
 * 'Icon shimmer' preference (#1979): same localStorage pattern, default on.
 * When OFF, Row and SectionContainer skip the sheen sweep. The user profile
 * (`preferences.shimmer`) is the durable copy; UserHeader adopts it on load
 * through setShimmer and writes it on toggle.
 */
const UiPrefsContext = createContext({
  uiTips: true,
  toggleUiTips: () => {},
  shimmer: true,
  setShimmer: () => {},
});

export const useUiPrefs = () => useContext(UiPrefsContext);

const readBool = (key, fallback) => {
  const saved = localStorage.getItem(key);
  return saved !== null ? JSON.parse(saved) : fallback;
};

export const UiPrefsProvider = ({ children }) => {
  const [uiTips, setUiTips] = useState(() => readBool('uiTips', true));
  const [shimmer, setShimmerState] = useState(() => readBool('shimmer', true));

  const toggleUiTips = () => {
    setUiTips((prev) => {
      const next = !prev;
      localStorage.setItem('uiTips', JSON.stringify(next));
      return next;
    });
  };

  // Stable identity: UserHeader lists it as an effect dependency.
  const setShimmer = useCallback((next) => {
    localStorage.setItem('shimmer', JSON.stringify(next));
    setShimmerState(next);
  }, []);

  return (
    <UiPrefsContext.Provider
      value={{ uiTips, toggleUiTips, shimmer, setShimmer }}
    >
      {children}
    </UiPrefsContext.Provider>
  );
};
